'use client';

import * as React from 'react';
import Link from 'next/link';
import {
  AnimatePresence,
  LayoutGroup,
  motion,
  type Transition
} from 'motion/react';
import {
  BrainCircuitIcon,
  ChevronRightIcon,
  EarIcon,
  EyeIcon,
  HeartPulseIcon,
  LandmarkIcon,
  MessagesSquareIcon,
  Mic2Icon,
  OrbitIcon,
  ShieldCheckIcon,
  WaypointsIcon,
  WrenchIcon
} from 'lucide-react';

import { useTheme } from '@workspace/ui/hooks/use-theme';

type RegionId =
  | 'main'
  | 'executive'
  | 'calculation'
  | 'communication'
  | 'speech'
  | 'vision'
  | 'memory'
  | 'quality'
  | 'scheduling'
  | 'workers'
  | 'selfHealing';

type Region = {
  id: RegionId;
  label: string;
  shortLabel: string;
  anatomy: string;
  cue: string;
  role: string;
  evidence: string;
  href: string;
  links?: Array<{ label: string; href: string }>;
  sx: number;
  sy: number;
  dz: number;
  radius: number;
  color: string;
  labelDx?: number;
  labelDy?: number;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
};

type CompactBrainModel = {
  scale: number;
  vertexCount: number;
  faceCount: number;
  p: number[];
  e: number[];
  f: number[];
};

type RenderData = {
  vertexCount: number;
  positions: Float32Array;
  regions: Float32Array;
  grains: Float32Array;
  lineCount: number;
  linePositions: Float32Array;
  lineRegions: Float32Array;
  lineGrains: Float32Array;
  faceCount: number;
  facePositions: Float32Array;
  faceRegions: Float32Array;
  faceGrains: Float32Array;
};

const MODEL_URL = '/command-cortex-lab/command-cortex-model.json';
const REGION_ACTIVE_BACKDROP_ID = 'command-cortex-v3-region-active-backdrop';
const REGION_CARD_LAYOUT_TRANSITION: Transition = {
  type: 'spring',
  stiffness: 430,
  damping: 40,
  mass: 0.78
};
const ACTIVE_REGION_CONTENT_TRANSITION: Transition = {
  duration: 0.32,
  ease: [0.22, 1, 0.36, 1]
};
const roundPercent = (value: number): number => Number(value.toFixed(4));
const BASE_YAW = 0.44;
const REGION_INDEX: Record<RegionId, number> = {
  main: 1,
  executive: 2,
  calculation: 3,
  communication: 4,
  speech: 5,
  vision: 6,
  memory: 7,
  quality: 8,
  scheduling: 9,
  workers: 10,
  selfHealing: 11
};

const REGIONS: Region[] = [
  {
    id: 'main',
    label: 'Conscious Main Agent',
    shortLabel: 'Main agent',
    anatomy: 'Prefrontal cortex',
    cue: 'Live dialogue + turn ownership',
    role: 'The live, coherent voice of the system. It integrates context, answers first, and stays responsible for the current turn.',
    evidence:
      'Viventium docs describe one main assistant in front: fast, coherent, and supported by specialized background systems only when they add value.',
    href: '/docs/concepts',
    links: [
      { label: 'Core concept', href: '/docs/concepts' },
      { label: 'System architecture', href: '/docs/architecture' }
    ],
    sx: 0.5,
    sy: 0.2,
    dz: 0.02,
    radius: 0.18,
    color: '#7dd3fc',
    labelDx: 4,
    labelDy: -2,
    icon: BrainCircuitIcon
  },
  {
    id: 'executive',
    label: 'Executive Planning',
    shortLabel: 'Executive',
    anatomy: 'Dorsolateral prefrontal + frontoparietal control',
    cue: 'Planning + sequencing',
    role: 'Plans, prioritizes, sequences, and coordinates which specialized AI systems should wake up.',
    evidence:
      'Background-agent docs describe activation detection, planning, sequencing, and independent follow-through without blocking the main reply.',
    href: '/docs/agents',
    links: [
      { label: 'Background cortices', href: '/docs/agents' },
      { label: 'Scheduling', href: '/docs/scheduling' }
    ],
    sx: 0.3,
    sy: 0.36,
    dz: -0.02,
    radius: 0.16,
    color: '#a78bfa',
    labelDx: 14,
    labelDy: -12,
    icon: WaypointsIcon
  },
  {
    id: 'calculation',
    label: 'Calculation + First Principles',
    shortLabel: 'Calculation',
    anatomy: 'Intraparietal sulcus / posterior parietal cortex',
    cue: 'Math, stats + first principles',
    role: 'Handles math, statistics, physics, quantitative checks, and structured reasoning.',
    evidence:
      'The product’s calculation/first-principles work maps naturally to parietal numerical cognition and spatial reasoning networks.',
    href: '/docs/agents',
    links: [{ label: 'Agent catalog', href: '/docs/agents' }],
    sx: -0.08,
    sy: 0.31,
    dz: -0.02,
    radius: 0.16,
    color: '#67e8f9',
    labelDx: 3,
    labelDy: 2,
    icon: LandmarkIcon
  },
  {
    id: 'communication',
    label: 'Communication + Prompt Workbench',
    shortLabel: 'Comms',
    anatomy: 'Frontotemporal language network',
    cue: 'Tone, Telegram + prompts',
    role: 'Keeps tone, channel behavior, Telegram continuity, and prompt governance aligned.',
    evidence:
      'Docs place chat, voice, Telegram, and Prompt Workbench inside one governed communication system with visible prompt QA and drift review.',
    href: '/docs/prompt-workbench',
    links: [
      { label: 'User interfaces', href: '/docs/interfaces' },
      { label: 'Prompt Workbench', href: '/docs/prompt-workbench' }
    ],
    sx: 0.25,
    sy: -0.07,
    dz: 0.04,
    radius: 0.16,
    color: '#5eead4',
    labelDx: 8,
    labelDy: 0,
    icon: MessagesSquareIcon
  },
  {
    id: 'speech',
    label: 'Speech, STT + TTS',
    shortLabel: 'Speech',
    anatomy: 'Perisylvian speech and auditory-motor loop',
    cue: 'Speech in, voice out',
    role: 'Turns speech into usable context, streams spoken replies, and respects Wing and Listen-Only boundaries.',
    evidence:
      'Voice docs require parity with text chat, interruptible live speech, Wing Mode, and Listen-Only Mode.',
    href: '/docs/interfaces',
    links: [{ label: 'Voice and channels', href: '/docs/interfaces' }],
    sx: 0.44,
    sy: -0.16,
    dz: 0.03,
    radius: 0.15,
    color: '#c4b5fd',
    labelDx: 8,
    labelDy: 4,
    icon: Mic2Icon
  },
  {
    id: 'vision',
    label: 'Vision, Browser + Research',
    shortLabel: 'Vision',
    anatomy: 'Occipital visual cortex + dorsal/ventral streams',
    cue: 'Browser, visual analysis + research',
    role: 'Looks outward: browser work, visual analysis, web research, comparisons, and live evidence.',
    evidence:
      'Deep Research and browser/analysis docs cover multi-step search, evidence gathering, and artifact-producing analysis.',
    href: '/docs/browser-and-analysis',
    links: [
      { label: 'Browser and analysis', href: '/docs/browser-and-analysis' },
      { label: 'Integrations', href: '/docs/integrations' }
    ],
    sx: -0.48,
    sy: 0.05,
    dz: 0.0,
    radius: 0.18,
    color: '#93c5fd',
    labelDx: 18,
    labelDy: -1,
    icon: EyeIcon
  },
  {
    id: 'memory',
    label: 'Episodic + Semantic Memory',
    shortLabel: 'Memory',
    anatomy: 'Hippocampal + medial temporal memory system',
    cue: 'Recall + continuity',
    role: 'Combines durable memory, conversation recall, transcript evidence, and semantic continuity without turning stale context into truth.',
    evidence:
      'Memory docs separate saved memories, conversation recall, meeting transcripts, and Listen-Only transcript evidence.',
    href: '/docs/memory',
    links: [{ label: 'Memory system', href: '/docs/memory' }],
    sx: -0.05,
    sy: -0.33,
    dz: 0.08,
    radius: 0.18,
    color: '#86efac',
    labelDx: 4,
    labelDy: 4,
    icon: OrbitIcon
  },
  {
    id: 'quality',
    label: 'Red Team + Bias Guard',
    shortLabel: 'Quality guard',
    anatomy: 'Anterior cingulate + anterior insula salience network',
    cue: 'Red team, bias + patterns',
    role: 'Detects conflict, challenges assumptions, pressure-tests plans, and switches attention toward what may be wrong or missing.',
    evidence:
      'Background Analysis, Confirmation Bias, Red Team, Pattern Recognition, and Emotional Resonance are separate background cortices.',
    href: '/docs/red-team',
    links: [
      { label: 'Red Team', href: '/docs/red-team' },
      { label: 'Agent catalog', href: '/docs/agents' }
    ],
    sx: 0.03,
    sy: 0.08,
    dz: 0.02,
    radius: 0.18,
    color: '#f0abfc',
    labelDx: 15,
    labelDy: -1,
    icon: ShieldCheckIcon
  },
  {
    id: 'scheduling',
    label: 'Scheduling + Self-Continuity',
    shortLabel: 'Scheduling',
    anatomy: 'Thalamic relay + rostral prefrontal timing',
    cue: 'Reminders + follow-ups',
    role: 'Keeps reminders, recurring research, briefings, and follow-ups tied to the right channel and time.',
    evidence:
      'Scheduling Cortex owns reminders, recurring jobs, catch-up policy, delivery ledgers, Nothing to Add suppression, and mobile dispatch.',
    href: '/docs/scheduling',
    links: [{ label: 'Scheduling', href: '/docs/scheduling' }],
    sx: 0.03,
    sy: -0.1,
    dz: 0.1,
    radius: 0.14,
    color: '#fde68a',
    labelDx: 6,
    labelDy: 7,
    icon: EarIcon
  },
  {
    id: 'workers',
    label: 'Workers + Project Execution',
    shortLabel: 'Workers',
    anatomy: 'Premotor / motor cortex with cerebellar correction',
    cue: 'Projects, workers + callbacks',
    role: 'Moves beyond chat into durable workspaces where AI workers can use browser, files, terminal, and callbacks.',
    evidence:
      'Projects and workers docs describe persistent workspaces that can be watched, steered, paused, or taken over.',
    href: '/docs/projects-workers',
    links: [{ label: 'Projects and workers', href: '/docs/projects-workers' }],
    sx: -0.36,
    sy: -0.47,
    dz: -0.02,
    radius: 0.17,
    color: '#fca5a5',
    labelDx: 8,
    labelDy: -12,
    icon: WrenchIcon
  },
  {
    id: 'selfHealing',
    label: 'Self-Healing + Feature Requests',
    shortLabel: 'Self-healing',
    anatomy: 'Brainstem and autonomic recovery loop',
    cue: 'Recovery + feature requests',
    role: 'Diagnoses, prepares bug reports, shapes feature requests, gates fixes, and keeps local recovery explicit.',
    evidence:
      'Self-healing and feature-request workflows run through explicit intake, isolated worktrees, review gates, and QA.',
    href: '/docs/self-healing-feature-requests',
    links: [
      {
        label: 'Self-healing and feature requests',
        href: '/docs/self-healing-feature-requests'
      }
    ],
    sx: -0.13,
    sy: -0.63,
    dz: -0.01,
    radius: 0.14,
    color: '#fb7185',
    labelDx: 25,
    labelDy: -2,
    icon: HeartPulseIcon
  }
];

const REGION_CYCLE_OFFSETS = REGIONS.map((_, index) => index / REGIONS.length);

let modelPromise: Promise<CompactBrainModel> | null = null;

function loadModel(): Promise<CompactBrainModel> {
  modelPromise ??= fetch(MODEL_URL).then((response) => {
    if (!response.ok) {
      throw new Error(
        `Unable to load command cortex model: ${response.status}`
      );
    }

    return response.json() as Promise<CompactBrainModel>;
  });

  return modelPromise;
}

function useCompactModel(): {
  model: CompactBrainModel | null;
  status: 'loading' | 'ready' | 'error';
} {
  const [model, setModel] = React.useState<CompactBrainModel | null>(null);
  const [status, setStatus] = React.useState<'loading' | 'ready' | 'error'>(
    'loading'
  );

  React.useEffect(() => {
    let mounted = true;

    loadModel()
      .then((loadedModel) => {
        if (mounted) {
          setModel(loadedModel);
          setStatus('ready');
        }
      })
      .catch(() => {
        if (mounted) {
          setModel(null);
          setStatus('error');
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  return { model, status };
}

function grain(index: number): number {
  return ((index * 9301 + 49297) % 233280) / 233280;
}

function regionForPoint(x: number, y: number, z: number): number {
  let best: { index: number; distance: number } | null = null;

  for (const region of REGIONS) {
    const dx = x - region.sx;
    const dy = y - region.sy;
    const dz = (z - region.dz) * 0.55;
    const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);

    if (distance < region.radius && (!best || distance < best.distance)) {
      best = { index: REGION_INDEX[region.id], distance };
    }
  }

  return best?.index ?? 0;
}

function buildRenderData(model: CompactBrainModel): RenderData {
  const vertexCount = Math.floor(model.p.length / 3);
  const positions = new Float32Array(model.p.length);
  const regions = new Float32Array(vertexCount);
  const grains = new Float32Array(vertexCount);

  for (let index = 0; index < vertexCount; index += 1) {
    const x = model.p[index * 3] / model.scale;
    const y = model.p[index * 3 + 1] / model.scale;
    const z = model.p[index * 3 + 2] / model.scale;
    positions[index * 3] = x;
    positions[index * 3 + 1] = y;
    positions[index * 3 + 2] = z;
    regions[index] = regionForPoint(x, y, z);
    grains[index] = grain(index);
  }

  const lineCount = model.e.length;
  const linePositions = new Float32Array(lineCount * 3);
  const lineRegions = new Float32Array(lineCount);
  const lineGrains = new Float32Array(lineCount);

  for (let index = 0; index < lineCount; index += 1) {
    const vertexIndex = model.e[index] ?? 0;
    linePositions[index * 3] = positions[vertexIndex * 3] ?? 0;
    linePositions[index * 3 + 1] = positions[vertexIndex * 3 + 1] ?? 0;
    linePositions[index * 3 + 2] = positions[vertexIndex * 3 + 2] ?? 0;
    lineRegions[index] = regions[vertexIndex] ?? 0;
    lineGrains[index] = grains[vertexIndex] ?? 0;
  }

  const faceCount = model.f.length;
  const facePositions = new Float32Array(faceCount * 3);
  const faceRegions = new Float32Array(faceCount);
  const faceGrains = new Float32Array(faceCount);

  for (let index = 0; index < faceCount; index += 1) {
    const vertexIndex = model.f[index] ?? 0;
    facePositions[index * 3] = positions[vertexIndex * 3] ?? 0;
    facePositions[index * 3 + 1] = positions[vertexIndex * 3 + 1] ?? 0;
    facePositions[index * 3 + 2] = positions[vertexIndex * 3 + 2] ?? 0;
    faceRegions[index] = regions[vertexIndex] ?? 0;
    faceGrains[index] = grains[vertexIndex] ?? 0;
  }

  return {
    vertexCount,
    positions,
    regions,
    grains,
    lineCount,
    linePositions,
    lineRegions,
    lineGrains,
    faceCount,
    facePositions,
    faceRegions,
    faceGrains
  };
}

const POINT_VERTEX_SHADER = `
  attribute vec3 aPosition;
  attribute float aRegion;
  attribute float aGrain;

  uniform float uTime;
  uniform float uPointBase;
  uniform float uDevicePixelRatio;
  uniform vec2 uFit;
  uniform vec2 uOffset;
  uniform vec2 uPointer;
  uniform float uPointerActive;
  uniform float uDarkMode;
  uniform float uActive[12];

  varying vec4 vColor;

  vec3 regionColor(float region) {
    if (region < 1.5) return vec3(0.490, 0.827, 0.988);
    if (region < 2.5) return vec3(0.655, 0.545, 0.980);
    if (region < 3.5) return vec3(0.404, 0.910, 0.976);
    if (region < 4.5) return vec3(0.369, 0.918, 0.831);
    if (region < 5.5) return vec3(0.769, 0.710, 0.992);
    if (region < 6.5) return vec3(0.576, 0.773, 0.992);
    if (region < 7.5) return vec3(0.525, 0.937, 0.675);
    if (region < 8.5) return vec3(0.941, 0.671, 0.988);
    if (region < 9.5) return vec3(0.992, 0.902, 0.541);
    if (region < 10.5) return vec3(0.988, 0.647, 0.647);
    return vec3(0.984, 0.445, 0.522);
  }

  float regionActive(float region) {
    if (region < 0.5) return 0.0;
    if (region < 1.5) return uActive[0];
    if (region < 2.5) return uActive[1];
    if (region < 3.5) return uActive[2];
    if (region < 4.5) return uActive[3];
    if (region < 5.5) return uActive[4];
    if (region < 6.5) return uActive[5];
    if (region < 7.5) return uActive[6];
    if (region < 8.5) return uActive[7];
    if (region < 9.5) return uActive[8];
    if (region < 10.5) return uActive[9];
    if (region < 11.5) return uActive[10];
    return 0.0;
  }

  void main() {
    float yaw = ${BASE_YAW.toFixed(3)} + sin(uTime * 0.22) * 0.018 + uPointer.x * uPointerActive * 0.13;
    float pitch = sin(uTime * 0.16) * 0.012 + uPointer.y * uPointerActive * 0.055;
    float cy = cos(yaw);
    float sy = sin(yaw);
    float rx = aPosition.x * cy + aPosition.z * sy * 0.52;
    float rz = -aPosition.x * sy + aPosition.z * cy;
    float cp = cos(pitch);
    float sp = sin(pitch);
    float ry = aPosition.y * cp - rz * sp * 0.36;
    rz = aPosition.y * sp * 0.36 + rz * cp;
    float depth = clamp((rz + 1.15) / 2.3, 0.0, 1.0);
    float screenX = rx * uFit.x + uOffset.x;
    float screenY = ry * uFit.y + uOffset.y;
    float pointer = uPointerActive * smoothstep(0.19, 0.035, distance(vec2(screenX, screenY), uPointer));
    float active = regionActive(aRegion);
    float pulseWave = 0.5 + 0.5 * sin(uTime * 3.2 + aGrain * 6.28318);
    float pulse = active * (0.28 + 0.58 * pulseWave);
    float breathe = 0.04 * sin(uTime * 1.1 + aPosition.x * 4.0);

    gl_Position = vec4(screenX, screenY, 0.0, 1.0);
    gl_PointSize = uPointBase * uDevicePixelRatio * (0.68 + depth * 0.76 + active * 0.86 + pulse * 0.62 + pointer * 0.28 + breathe);

    vec3 darkBase = vec3(0.38 + depth * 0.22, 0.57 + depth * 0.18, 0.82 + depth * 0.14);
    vec3 lightBase = vec3(0.045 + depth * 0.155, 0.16 + depth * 0.18, 0.31 + depth * 0.18);
    vec3 base = mix(lightBase, darkBase, uDarkMode);
    float alpha = mix(0.58 + depth * 0.32, 0.44 + depth * 0.32, uDarkMode);

    if (active > 0.02) {
      base = mix(base, regionColor(aRegion), 0.74 + active * 0.18);
      alpha = max(alpha, 0.66 + active * 0.26 + pulse * 0.2);
    }

    if (pointer > 0.01) {
      vec3 pointerColor = mix(vec3(0.02, 0.36, 0.38), vec3(0.9, 0.99, 0.95), uDarkMode);
      base = mix(base, pointerColor, clamp(pointer * 0.32, 0.0, 0.34));
      alpha += pointer * 0.1;
    }

    vColor = vec4(base, clamp(alpha, 0.0, 0.92));
  }
`;

const POINT_FRAGMENT_SHADER = `
  precision mediump float;
  varying vec4 vColor;

  void main() {
    vec2 point = gl_PointCoord.xy - vec2(0.5);
    float distanceToCenter = length(point);
    if (distanceToCenter > 0.5) {
      discard;
    }
    float core = smoothstep(0.5, 0.28, distanceToCenter);
    gl_FragColor = vec4(vColor.rgb, vColor.a * core);
  }
`;

const LINE_VERTEX_SHADER = `
  attribute vec3 aPosition;
  attribute float aRegion;
  attribute float aGrain;

  uniform float uTime;
  uniform vec2 uFit;
  uniform vec2 uOffset;
  uniform vec2 uPointer;
  uniform float uPointerActive;
  uniform float uDarkMode;
  uniform float uActive[12];

  varying vec4 vColor;

  vec3 regionColor(float region) {
    if (region < 1.5) return vec3(0.490, 0.827, 0.988);
    if (region < 2.5) return vec3(0.655, 0.545, 0.980);
    if (region < 3.5) return vec3(0.404, 0.910, 0.976);
    if (region < 4.5) return vec3(0.369, 0.918, 0.831);
    if (region < 5.5) return vec3(0.769, 0.710, 0.992);
    if (region < 6.5) return vec3(0.576, 0.773, 0.992);
    if (region < 7.5) return vec3(0.525, 0.937, 0.675);
    if (region < 8.5) return vec3(0.941, 0.671, 0.988);
    if (region < 9.5) return vec3(0.992, 0.902, 0.541);
    if (region < 10.5) return vec3(0.988, 0.647, 0.647);
    return vec3(0.984, 0.445, 0.522);
  }

  float regionActive(float region) {
    if (region < 0.5) return 0.0;
    if (region < 1.5) return uActive[0];
    if (region < 2.5) return uActive[1];
    if (region < 3.5) return uActive[2];
    if (region < 4.5) return uActive[3];
    if (region < 5.5) return uActive[4];
    if (region < 6.5) return uActive[5];
    if (region < 7.5) return uActive[6];
    if (region < 8.5) return uActive[7];
    if (region < 9.5) return uActive[8];
    if (region < 10.5) return uActive[9];
    if (region < 11.5) return uActive[10];
    return 0.0;
  }

  void main() {
    float yaw = ${BASE_YAW.toFixed(3)} + sin(uTime * 0.22) * 0.018 + uPointer.x * uPointerActive * 0.13;
    float pitch = sin(uTime * 0.16) * 0.012 + uPointer.y * uPointerActive * 0.055;
    float cy = cos(yaw);
    float sy = sin(yaw);
    float rx = aPosition.x * cy + aPosition.z * sy * 0.52;
    float rz = -aPosition.x * sy + aPosition.z * cy;
    float cp = cos(pitch);
    float sp = sin(pitch);
    float ry = aPosition.y * cp - rz * sp * 0.36;
    rz = aPosition.y * sp * 0.36 + rz * cp;
    float depth = clamp((rz + 1.15) / 2.3, 0.0, 1.0);
    float screenX = rx * uFit.x + uOffset.x;
    float screenY = ry * uFit.y + uOffset.y;
    float pointer = uPointerActive * smoothstep(0.2, 0.04, distance(vec2(screenX, screenY), uPointer));
    float active = regionActive(aRegion);
    float pulseWave = 0.5 + 0.5 * sin(uTime * 3.0 + aGrain * 6.28318);
    float pulse = active * (0.2 + 0.44 * pulseWave);

    gl_Position = vec4(screenX, screenY, 0.0, 1.0);

    vec3 darkBase = vec3(0.22 + depth * 0.16, 0.48 + depth * 0.2, 0.72 + depth * 0.16);
    vec3 lightBase = vec3(0.04 + depth * 0.1, 0.15 + depth * 0.16, 0.29 + depth * 0.15);
    vec3 base = mix(lightBase, darkBase, uDarkMode);
    float alpha = mix(0.42 * (0.78 + depth * 0.74), 0.46 * (0.58 + depth * 0.7), uDarkMode);

    if (active > 0.02) {
      base = mix(base, regionColor(aRegion), 0.7 + active * 0.16);
      alpha += 0.15 + pulse * 0.28;
    }

    if (pointer > 0.01) {
      vec3 pointerColor = mix(vec3(0.02, 0.34, 0.36), vec3(0.82, 0.98, 0.92), uDarkMode);
      base = mix(base, pointerColor, clamp(pointer * 0.26, 0.0, 0.3));
      alpha += pointer * 0.1;
    }

    vColor = vec4(base, clamp(alpha, 0.0, 0.72));
  }
`;

const BASIC_FRAGMENT_SHADER = `
  precision mediump float;
  varying vec4 vColor;

  void main() {
    gl_FragColor = vColor;
  }
`;

const SURFACE_VERTEX_SHADER = `
  attribute vec3 aPosition;
  attribute float aRegion;
  attribute float aGrain;

  uniform float uTime;
  uniform vec2 uFit;
  uniform vec2 uOffset;
  uniform vec2 uPointer;
  uniform float uPointerActive;
  uniform float uDarkMode;
  uniform float uActive[12];

  varying vec4 vColor;

  vec3 regionColor(float region) {
    if (region < 1.5) return vec3(0.490, 0.827, 0.988);
    if (region < 2.5) return vec3(0.655, 0.545, 0.980);
    if (region < 3.5) return vec3(0.404, 0.910, 0.976);
    if (region < 4.5) return vec3(0.369, 0.918, 0.831);
    if (region < 5.5) return vec3(0.769, 0.710, 0.992);
    if (region < 6.5) return vec3(0.576, 0.773, 0.992);
    if (region < 7.5) return vec3(0.525, 0.937, 0.675);
    if (region < 8.5) return vec3(0.941, 0.671, 0.988);
    if (region < 9.5) return vec3(0.992, 0.902, 0.541);
    if (region < 10.5) return vec3(0.988, 0.647, 0.647);
    return vec3(0.984, 0.445, 0.522);
  }

  float regionActive(float region) {
    if (region < 0.5) return 0.0;
    if (region < 1.5) return uActive[0];
    if (region < 2.5) return uActive[1];
    if (region < 3.5) return uActive[2];
    if (region < 4.5) return uActive[3];
    if (region < 5.5) return uActive[4];
    if (region < 6.5) return uActive[5];
    if (region < 7.5) return uActive[6];
    if (region < 8.5) return uActive[7];
    if (region < 9.5) return uActive[8];
    if (region < 10.5) return uActive[9];
    if (region < 11.5) return uActive[10];
    return 0.0;
  }

  void main() {
    float yaw = ${BASE_YAW.toFixed(3)} + sin(uTime * 0.22) * 0.018 + uPointer.x * uPointerActive * 0.13;
    float pitch = sin(uTime * 0.16) * 0.012 + uPointer.y * uPointerActive * 0.055;
    float cy = cos(yaw);
    float sy = sin(yaw);
    float rx = aPosition.x * cy + aPosition.z * sy * 0.52;
    float rz = -aPosition.x * sy + aPosition.z * cy;
    float cp = cos(pitch);
    float sp = sin(pitch);
    float ry = aPosition.y * cp - rz * sp * 0.36;
    rz = aPosition.y * sp * 0.36 + rz * cp;
    float depth = clamp((rz + 1.15) / 2.3, 0.0, 1.0);
    float screenX = rx * uFit.x + uOffset.x;
    float screenY = ry * uFit.y + uOffset.y;
    float pointer = uPointerActive * smoothstep(0.2, 0.04, distance(vec2(screenX, screenY), uPointer));
    float active = regionActive(aRegion);
    float pulseWave = 0.5 + 0.5 * sin(uTime * 2.8 + aGrain * 6.28318);
    float pulse = active * (0.12 + 0.3 * pulseWave);

    gl_Position = vec4(screenX, screenY, 0.0, 1.0);

    vec3 darkBase = vec3(0.05 + depth * 0.08, 0.13 + depth * 0.11, 0.22 + depth * 0.14);
    vec3 lightBase = vec3(0.78 - depth * 0.16, 0.89 - depth * 0.11, 0.98 - depth * 0.08);
    vec3 base = mix(lightBase, darkBase, uDarkMode);
    float alpha = mix(0.135 * (0.62 + depth * 0.5), 0.1 * (0.55 + depth * 0.55), uDarkMode);

    if (active > 0.02) {
      base = mix(base, regionColor(aRegion), 0.28 + active * 0.2);
      alpha += 0.045 + pulse * 0.11;
    }

    if (pointer > 0.01) {
      vec3 pointerColor = mix(vec3(0.74, 0.94, 0.92), vec3(0.48, 0.72, 0.66), uDarkMode);
      base = mix(base, pointerColor, clamp(pointer * 0.16, 0.0, 0.2));
      alpha += pointer * 0.03;
    }

    vColor = vec4(base, clamp(alpha, 0.0, 0.24));
  }
`;

function compileShader(
  gl: WebGLRenderingContext,
  type: number,
  source: string
): WebGLShader {
  const shader = gl.createShader(type);

  if (!shader) {
    throw new Error('Unable to create shader');
  }

  gl.shaderSource(shader, source);
  gl.compileShader(shader);

  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const message = gl.getShaderInfoLog(shader) ?? 'Unknown shader error';
    gl.deleteShader(shader);
    throw new Error(message);
  }

  return shader;
}

function createProgram(
  gl: WebGLRenderingContext,
  vertexSource: string,
  fragmentSource: string
): WebGLProgram {
  const program = gl.createProgram();

  if (!program) {
    throw new Error('Unable to create program');
  }

  const vertex = compileShader(gl, gl.VERTEX_SHADER, vertexSource);
  const fragment = compileShader(gl, gl.FRAGMENT_SHADER, fragmentSource);
  gl.attachShader(program, vertex);
  gl.attachShader(program, fragment);
  gl.linkProgram(program);
  gl.deleteShader(vertex);
  gl.deleteShader(fragment);

  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const message = gl.getProgramInfoLog(program) ?? 'Unknown program error';
    gl.deleteProgram(program);
    throw new Error(message);
  }

  return program;
}

function projectRegion(region: Region): { x: number; y: number } {
  const yawCos = Math.cos(BASE_YAW);
  const yawSin = Math.sin(BASE_YAW);
  const projectedX = region.sx * yawCos + region.dz * yawSin * 0.52;
  const projectedY = region.sy;

  return {
    x: roundPercent(51 + projectedX * 38 + (region.labelDx ?? 0)),
    y: roundPercent(51 - projectedY * 40 + (region.labelDy ?? 0))
  };
}

function useReducedMotion(): boolean {
  const [reduced, setReduced] = React.useState(false);

  React.useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(query.matches);
    update();
    query.addEventListener('change', update);

    return () => query.removeEventListener('change', update);
  }, []);

  return reduced;
}

function useResolvedDarkMode(): boolean {
  const { resolvedTheme } = useTheme();
  const [systemDark, setSystemDark] = React.useState(false);

  React.useEffect(() => {
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const update = () => setSystemDark(query.matches);
    update();
    query.addEventListener('change', update);

    return () => query.removeEventListener('change', update);
  }, []);

  return resolvedTheme ? resolvedTheme === 'dark' : systemDark;
}

function BrainMapFallback({
  title,
  detail
}: {
  title: string;
  detail: string;
}): React.JSX.Element {
  return (
    <div className="absolute inset-0 flex items-center justify-center p-8">
      <div className="max-w-sm rounded-lg border border-slate-900/10 bg-white/55 p-5 text-center shadow-[0_0_36px_rgba(14,165,233,0.12)] backdrop-blur-md dark:border-white/12 dark:bg-black/42">
        <p className="text-sm font-semibold text-slate-950 dark:text-white">
          {title}
        </p>
        <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-400">
          {detail}
        </p>
      </div>
    </div>
  );
}

function CommandBrainCanvas({
  data,
  selectedId,
  hoveredId,
  darkMode
}: {
  data: RenderData;
  selectedId: RegionId;
  hoveredId: RegionId | null;
  darkMode: boolean;
}): React.JSX.Element {
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const pointerRef = React.useRef({ x: 0, y: 0, active: 0 });
  const selectedRef = React.useRef(selectedId);
  const hoveredRef = React.useRef(hoveredId);
  const reducedMotion = useReducedMotion();
  const [contextFailed, setContextFailed] = React.useState(false);

  React.useEffect(() => {
    selectedRef.current = selectedId;
  }, [selectedId]);

  React.useEffect(() => {
    hoveredRef.current = hoveredId;
  }, [hoveredId]);

  React.useEffect(() => {
    const canvas = canvasRef.current;

    if (!canvas) {
      return;
    }

    const handleContextLost = (event: Event) => {
      event.preventDefault();
      setContextFailed(true);
    };

    canvas.addEventListener('webglcontextlost', handleContextLost);

    const gl = canvas.getContext('webgl', {
      alpha: true,
      antialias: true,
      depth: false,
      premultipliedAlpha: false,
      powerPreference: 'low-power'
    });

    if (!gl) {
      setContextFailed(true);

      return () => {
        canvas.removeEventListener('webglcontextlost', handleContextLost);
      };
    }

    const pointProgram = createProgram(
      gl,
      POINT_VERTEX_SHADER,
      POINT_FRAGMENT_SHADER
    );
    const lineProgram = createProgram(
      gl,
      LINE_VERTEX_SHADER,
      BASIC_FRAGMENT_SHADER
    );
    const surfaceProgram = createProgram(
      gl,
      SURFACE_VERTEX_SHADER,
      BASIC_FRAGMENT_SHADER
    );
    const buffers = {
      points: gl.createBuffer(),
      pointRegions: gl.createBuffer(),
      pointGrains: gl.createBuffer(),
      lines: gl.createBuffer(),
      lineRegions: gl.createBuffer(),
      lineGrains: gl.createBuffer(),
      faces: gl.createBuffer(),
      faceRegions: gl.createBuffer(),
      faceGrains: gl.createBuffer()
    };

    if (Object.values(buffers).some((buffer) => !buffer)) {
      return;
    }

    const bindAttribute = (
      program: WebGLProgram,
      attribute: string,
      buffer: WebGLBuffer,
      values: Float32Array,
      size: number
    ) => {
      const location = gl.getAttribLocation(program, attribute);
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.bufferData(gl.ARRAY_BUFFER, values, gl.STATIC_DRAW);
      gl.enableVertexAttribArray(location);
      gl.vertexAttribPointer(location, size, gl.FLOAT, false, 0, 0);

      return location;
    };

    gl.useProgram(pointProgram);
    const pointLocations = {
      position: bindAttribute(
        pointProgram,
        'aPosition',
        buffers.points as WebGLBuffer,
        data.positions,
        3
      ),
      region: bindAttribute(
        pointProgram,
        'aRegion',
        buffers.pointRegions as WebGLBuffer,
        data.regions,
        1
      ),
      grain: bindAttribute(
        pointProgram,
        'aGrain',
        buffers.pointGrains as WebGLBuffer,
        data.grains,
        1
      )
    };

    gl.useProgram(lineProgram);
    const lineLocations = {
      position: bindAttribute(
        lineProgram,
        'aPosition',
        buffers.lines as WebGLBuffer,
        data.linePositions,
        3
      ),
      region: bindAttribute(
        lineProgram,
        'aRegion',
        buffers.lineRegions as WebGLBuffer,
        data.lineRegions,
        1
      ),
      grain: bindAttribute(
        lineProgram,
        'aGrain',
        buffers.lineGrains as WebGLBuffer,
        data.lineGrains,
        1
      )
    };

    gl.useProgram(surfaceProgram);
    const surfaceLocations = {
      position: bindAttribute(
        surfaceProgram,
        'aPosition',
        buffers.faces as WebGLBuffer,
        data.facePositions,
        3
      ),
      region: bindAttribute(
        surfaceProgram,
        'aRegion',
        buffers.faceRegions as WebGLBuffer,
        data.faceRegions,
        1
      ),
      grain: bindAttribute(
        surfaceProgram,
        'aGrain',
        buffers.faceGrains as WebGLBuffer,
        data.faceGrains,
        1
      )
    };

    const pointUniforms = {
      time: gl.getUniformLocation(pointProgram, 'uTime'),
      pointBase: gl.getUniformLocation(pointProgram, 'uPointBase'),
      dpr: gl.getUniformLocation(pointProgram, 'uDevicePixelRatio'),
      fit: gl.getUniformLocation(pointProgram, 'uFit'),
      offset: gl.getUniformLocation(pointProgram, 'uOffset'),
      pointer: gl.getUniformLocation(pointProgram, 'uPointer'),
      pointerActive: gl.getUniformLocation(pointProgram, 'uPointerActive'),
      darkMode: gl.getUniformLocation(pointProgram, 'uDarkMode'),
      active: gl.getUniformLocation(pointProgram, 'uActive')
    };
    const lineUniforms = {
      time: gl.getUniformLocation(lineProgram, 'uTime'),
      fit: gl.getUniformLocation(lineProgram, 'uFit'),
      offset: gl.getUniformLocation(lineProgram, 'uOffset'),
      pointer: gl.getUniformLocation(lineProgram, 'uPointer'),
      pointerActive: gl.getUniformLocation(lineProgram, 'uPointerActive'),
      darkMode: gl.getUniformLocation(lineProgram, 'uDarkMode'),
      active: gl.getUniformLocation(lineProgram, 'uActive')
    };
    const surfaceUniforms = {
      time: gl.getUniformLocation(surfaceProgram, 'uTime'),
      fit: gl.getUniformLocation(surfaceProgram, 'uFit'),
      offset: gl.getUniformLocation(surfaceProgram, 'uOffset'),
      pointer: gl.getUniformLocation(surfaceProgram, 'uPointer'),
      pointerActive: gl.getUniformLocation(surfaceProgram, 'uPointerActive'),
      darkMode: gl.getUniformLocation(surfaceProgram, 'uDarkMode'),
      active: gl.getUniformLocation(surfaceProgram, 'uActive')
    };

    gl.enable(gl.BLEND);
    gl.disable(gl.DEPTH_TEST);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    let width = 0;
    let height = 0;
    let dpr = 1;
    let frame = 0;
    let live = true;
    let lastPaint = 0;
    let viewportVisible = true;
    let pageVisible = document.visibilityState !== 'hidden';

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      dpr = Math.min(window.devicePixelRatio || 1, 1.8);
      width = Math.max(1, Math.floor(rect.width));
      height = Math.max(1, Math.floor(rect.height));
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      gl.viewport(0, 0, canvas.width, canvas.height);
    };

    const updatePointer = (event: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      const inside =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;

      if (!inside) {
        pointerRef.current = { x: 0, y: 0, active: 0 };
        return;
      }

      pointerRef.current = {
        x: ((event.clientX - rect.left) / rect.width - 0.5) * 2,
        y: -(((event.clientY - rect.top) / rect.height - 0.5) * 2),
        active: 1
      };
    };

    const resetPointer = () => {
      pointerRef.current = { x: 0, y: 0, active: 0 };
    };

    const activeFlagBuffer = new Float32Array(12);
    const activeFlags = (time: number) => {
      activeFlagBuffer.fill(0);
      const selected = selectedRef.current;
      const hovered = hoveredRef.current;
      for (let index = 0; index < REGIONS.length; index += 1) {
        const region = REGIONS[index];

        if (!region) {
          continue;
        }

        const selectedAmount =
          region.id === selected
            ? 0.88 + 0.12 * Math.sin(time * 0.0032 + index * 0.7)
            : 0;
        const hoveredAmount = region.id === hovered ? 0.55 : 0;
        const cycle = (time * 0.00024 + (REGION_CYCLE_OFFSETS[index] ?? 0)) % 1;
        const flow = 1 - Math.abs(cycle - 0.5) * 2;
        const drift =
          !hovered && region.id !== selected && flow > 0.62
            ? Math.pow((flow - 0.62) / 0.38, 2) * 0.24
            : 0;
        activeFlagBuffer[index] = Math.min(
          1,
          selectedAmount + hoveredAmount + drift
        );
      }

      return activeFlagBuffer;
    };

    const shouldRender = () => viewportVisible && pageVisible;
    const scheduleFrame = () => {
      if (!reducedMotion && live && frame === 0) {
        frame = requestAnimationFrame(draw);
      }
    };

    const draw = (time: number) => {
      frame = 0;

      if (!live) {
        return;
      }

      if (!shouldRender()) {
        return;
      }

      scheduleFrame();

      if (!reducedMotion && time - lastPaint < 33) {
        return;
      }

      lastPaint = time;

      const compact = width < 640;
      const fitY = compact ? 0.62 : 0.9;
      const aspectFitX = fitY * Math.min(1, height / width);
      const fitX = compact ? 0.7 : Math.min(0.9, aspectFitX * 1.32);
      const offsetX = compact ? 0.05 : 0.02;
      const pointer = pointerRef.current;
      const flags = activeFlags(time);
      const renderTime = reducedMotion ? 0 : time * 0.001;
      const darkModeValue = darkMode ? 1 : 0;

      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);

      gl.useProgram(surfaceProgram);
      gl.bindBuffer(gl.ARRAY_BUFFER, buffers.faces);
      gl.vertexAttribPointer(
        surfaceLocations.position,
        3,
        gl.FLOAT,
        false,
        0,
        0
      );
      gl.bindBuffer(gl.ARRAY_BUFFER, buffers.faceRegions);
      gl.vertexAttribPointer(surfaceLocations.region, 1, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, buffers.faceGrains);
      gl.vertexAttribPointer(surfaceLocations.grain, 1, gl.FLOAT, false, 0, 0);
      gl.uniform1f(surfaceUniforms.time, renderTime);
      gl.uniform2f(surfaceUniforms.fit, fitX, fitY);
      gl.uniform2f(surfaceUniforms.offset, offsetX, compact ? -0.05 : -0.02);
      gl.uniform2f(surfaceUniforms.pointer, pointer.x, pointer.y);
      gl.uniform1f(surfaceUniforms.pointerActive, pointer.active);
      gl.uniform1f(surfaceUniforms.darkMode, darkModeValue);
      gl.uniform1fv(surfaceUniforms.active, flags);
      gl.drawArrays(gl.TRIANGLES, 0, data.faceCount);

      gl.useProgram(lineProgram);
      gl.bindBuffer(gl.ARRAY_BUFFER, buffers.lines);
      gl.vertexAttribPointer(lineLocations.position, 3, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, buffers.lineRegions);
      gl.vertexAttribPointer(lineLocations.region, 1, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, buffers.lineGrains);
      gl.vertexAttribPointer(lineLocations.grain, 1, gl.FLOAT, false, 0, 0);
      gl.uniform1f(lineUniforms.time, renderTime);
      gl.uniform2f(lineUniforms.fit, fitX, fitY);
      gl.uniform2f(lineUniforms.offset, offsetX, compact ? -0.05 : -0.02);
      gl.uniform2f(lineUniforms.pointer, pointer.x, pointer.y);
      gl.uniform1f(lineUniforms.pointerActive, pointer.active);
      gl.uniform1f(lineUniforms.darkMode, darkModeValue);
      gl.uniform1fv(lineUniforms.active, flags);
      gl.drawArrays(gl.LINES, 0, data.lineCount);

      gl.useProgram(pointProgram);
      gl.bindBuffer(gl.ARRAY_BUFFER, buffers.points);
      gl.vertexAttribPointer(pointLocations.position, 3, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, buffers.pointRegions);
      gl.vertexAttribPointer(pointLocations.region, 1, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, buffers.pointGrains);
      gl.vertexAttribPointer(pointLocations.grain, 1, gl.FLOAT, false, 0, 0);
      gl.uniform1f(pointUniforms.time, renderTime);
      gl.uniform1f(pointUniforms.pointBase, compact ? 1.45 : 2.16);
      gl.uniform1f(pointUniforms.dpr, dpr);
      gl.uniform2f(pointUniforms.fit, fitX, fitY);
      gl.uniform2f(pointUniforms.offset, offsetX, compact ? -0.05 : -0.02);
      gl.uniform2f(pointUniforms.pointer, pointer.x, pointer.y);
      gl.uniform1f(pointUniforms.pointerActive, pointer.active);
      gl.uniform1f(pointUniforms.darkMode, darkModeValue);
      gl.uniform1fv(pointUniforms.active, flags);
      gl.drawArrays(gl.POINTS, 0, data.vertexCount);
    };

    resize();

    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    const visibilityObserver =
      typeof IntersectionObserver === 'undefined'
        ? null
        : new IntersectionObserver(
            ([entry]) => {
              viewportVisible = entry?.isIntersecting ?? true;

              if (viewportVisible) {
                scheduleFrame();
              } else {
                resetPointer();
              }
            },
            { threshold: 0.01 }
          );
    visibilityObserver?.observe(canvas);
    const updatePageVisibility = () => {
      pageVisible = document.visibilityState !== 'hidden';

      if (pageVisible) {
        scheduleFrame();
      } else {
        resetPointer();
      }
    };
    document.addEventListener('visibilitychange', updatePageVisibility);
    window.addEventListener('pointermove', updatePointer, { passive: true });
    window.addEventListener('pointercancel', resetPointer);
    window.addEventListener('blur', resetPointer);

    frame = requestAnimationFrame(draw);

    return () => {
      live = false;
      cancelAnimationFrame(frame);
      observer.disconnect();
      visibilityObserver?.disconnect();
      document.removeEventListener('visibilitychange', updatePageVisibility);
      window.removeEventListener('pointermove', updatePointer);
      window.removeEventListener('pointercancel', resetPointer);
      window.removeEventListener('blur', resetPointer);
      canvas.removeEventListener('webglcontextlost', handleContextLost);
      Object.values(buffers).forEach((buffer) => {
        if (buffer) {
          gl.deleteBuffer(buffer);
        }
      });
      gl.deleteProgram(pointProgram);
      gl.deleteProgram(lineProgram);
      gl.deleteProgram(surfaceProgram);
    };
  }, [darkMode, data, reducedMotion]);

  if (contextFailed) {
    return (
      <BrainMapFallback
        title="Cortex map paused"
        detail="The region list still works; reload the page to restart the live brain map."
      />
    );
  }

  return (
    <canvas
      ref={canvasRef}
      aria-label="Command Cortex interactive brain map"
      className="absolute inset-0 h-full w-full"
    />
  );
}

function RegionLabel({
  region,
  selected,
  onSelect,
  onHover
}: {
  region: Region;
  selected: boolean;
  onSelect: (region: RegionId) => void;
  onHover: (region: RegionId | null) => void;
}): React.JSX.Element {
  const position = projectRegion(region);

  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={() => onSelect(region.id)}
      onPointerEnter={() => onHover(region.id)}
      onPointerLeave={() => onHover(null)}
      onFocus={() => onHover(region.id)}
      onBlur={() => onHover(null)}
      className={[
        'absolute z-10 hidden -translate-x-1/2 -translate-y-1/2 rounded-md border px-2 py-1 text-[10px] font-bold shadow-[0_0_20px_rgba(0,0,0,0.38)] transition lg:block',
        selected
          ? 'border-slate-950/70 bg-slate-950 text-white dark:border-white/70 dark:bg-white dark:text-slate-950'
          : 'border-slate-900/14 bg-white/64 text-slate-800 hover:border-slate-900/30 hover:bg-white/80 dark:border-white/16 dark:bg-black/62 dark:text-slate-100 dark:hover:border-white/42 dark:hover:bg-black/78'
      ].join(' ')}
      style={{
        left: `${position.x}%`,
        top: `${position.y}%`
      }}
    >
      {region.shortLabel}
    </button>
  );
}

function RegionButton({
  region,
  selected,
  onSelect,
  onHover
}: {
  region: Region;
  selected: boolean;
  onSelect: (region: RegionId) => void;
  onHover: (region: RegionId | null) => void;
}): React.JSX.Element {
  const Icon = region.icon;

  return (
    <motion.button
      layout="position"
      initial={false}
      animate={{
        scale: selected ? 1.012 : 1,
        y: selected ? -1 : 0
      }}
      transition={REGION_CARD_LAYOUT_TRANSITION}
      type="button"
      aria-pressed={selected}
      onClick={() => onSelect(region.id)}
      onPointerEnter={() => onHover(region.id)}
      onPointerLeave={() => onHover(null)}
      onFocus={() => onHover(region.id)}
      onBlur={() => onHover(null)}
      className={[
        'pointer-events-auto relative isolate grid grid-cols-[auto_minmax(0,1fr)] gap-2 overflow-hidden rounded-lg border p-1.5 text-left transition-[border-color,background-color,box-shadow,color,transform] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform',
        selected
          ? 'border-sky-500/34 bg-white/[0.62] text-slate-950 shadow-[0_0_30px_rgba(14,116,144,0.12)] backdrop-blur-md dark:border-white/34 dark:bg-white/[0.075] dark:text-white dark:shadow-[0_0_28px_rgba(125,211,252,0.12)]'
          : 'border-slate-900/10 bg-white/[0.48] text-slate-900 backdrop-blur-[2px] hover:border-sky-600/30 hover:bg-white/[0.66] dark:border-white/10 dark:bg-white/[0.045] dark:text-white dark:hover:border-white/28 dark:hover:bg-white/[0.075]'
      ].join(' ')}
    >
      {selected ? (
        <motion.span
          layoutId={REGION_ACTIVE_BACKDROP_ID}
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-0 rounded-[inherit] border border-sky-500/18 bg-[linear-gradient(105deg,rgba(255,255,255,0.28),rgba(14,165,233,0.10)_52%,rgba(255,255,255,0.16))] shadow-[inset_0_1px_0_rgba(255,255,255,0.42),0_0_24px_rgba(14,165,233,0.14)] dark:border-sky-100/20 dark:bg-[linear-gradient(105deg,rgba(255,255,255,0.12),rgba(14,165,233,0.11)_52%,rgba(255,255,255,0.04))] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.14),0_0_26px_rgba(125,211,252,0.13)]"
          transition={REGION_CARD_LAYOUT_TRANSITION}
        />
      ) : null}
      <span
        className={[
          'relative z-10 mt-0.5 flex size-6 items-center justify-center rounded-md transition-[background-color,color,box-shadow,transform] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]',
          selected
            ? 'bg-slate-950 text-white shadow-[0_0_18px_rgba(14,165,233,0.18)] dark:bg-white dark:text-slate-950'
            : 'bg-white/58 dark:bg-black/26'
        ].join(' ')}
        style={selected ? undefined : { color: region.color }}
      >
        <Icon
          aria-hidden="true"
          className="size-3.5"
        />
      </span>
      <span className="relative z-10 min-w-0">
        <span className="block text-[13px] font-semibold leading-4 tracking-normal">
          {region.label}
        </span>
        <span
          className={[
            'mt-0.5 block text-[11px] leading-4 transition-colors duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]',
            selected
              ? 'text-slate-700 dark:text-slate-200'
              : 'text-slate-600 dark:text-slate-400'
          ].join(' ')}
        >
          {region.cue}
        </span>
      </span>
    </motion.button>
  );
}

function ActiveRegionCard({
  region,
  className = '',
  onPause
}: {
  region: Region;
  className?: string;
  onPause?: () => void;
}): React.JSX.Element {
  const Icon = region.icon;
  const reducedMotion = useReducedMotion();

  return (
    <div
      onPointerEnter={onPause}
      onPointerMove={onPause}
      onMouseEnter={onPause}
      onMouseMove={onPause}
      onFocusCapture={onPause}
      className={[
        'pointer-events-auto w-full max-w-[25rem] rounded-lg border border-slate-900/10 bg-white/[0.42] p-3 shadow-[0_0_36px_rgba(14,116,144,0.1)] backdrop-blur-[2px] dark:border-white/10 dark:bg-black/[0.18] dark:shadow-[0_0_36px_rgba(14,165,233,0.08)]',
        className
      ].join(' ')}
    >
      <AnimatePresence
        initial={false}
        mode="popLayout"
      >
        <motion.div
          key={region.id}
          layout
          initial={reducedMotion ? false : { opacity: 0, y: 7 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -7 }}
          transition={
            reducedMotion
              ? { duration: 0.01 }
              : ACTIVE_REGION_CONTENT_TRANSITION
          }
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase text-slate-500">
                Active region
              </p>
              <h2 className="mt-1.5 text-xl font-semibold leading-tight text-slate-950 dark:text-white">
                {region.label}
              </h2>
              <p className="mt-1 text-xs font-semibold text-slate-600 dark:text-slate-400">
                {region.anatomy}
              </p>
            </div>
            <Icon
              aria-hidden="true"
              className="size-7 shrink-0"
              style={{ color: region.color }}
            />
          </div>
          <p className="mt-2 text-sm leading-5 text-slate-800 dark:text-slate-300">
            {region.role}
          </p>
          <p className="mt-2 text-xs leading-5 text-slate-700/85 dark:text-slate-300/82">
            {region.evidence}
          </p>
          <Link
            href={region.href}
            className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-sky-700 transition hover:text-slate-950 dark:text-sky-200 dark:hover:text-white"
          >
            Read the related docs
            <ChevronRightIcon className="size-4" />
          </Link>
          {region.links && region.links.length > 1 ? (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {region.links.map((link) => (
                <Link
                  key={`${region.id}-${link.href}`}
                  href={link.href}
                  className="rounded-md border border-slate-900/10 bg-white/20 px-2 py-1 text-[11px] font-semibold text-slate-700 transition hover:border-slate-900/28 hover:text-slate-950 dark:border-white/10 dark:bg-black/10 dark:text-slate-300 dark:hover:border-white/30 dark:hover:text-white"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          ) : null}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

export function CommandCortexCenteredV3Lab(): React.JSX.Element {
  const { model, status } = useCompactModel();
  const renderData = React.useMemo(
    () => (model ? buildRenderData(model) : null),
    [model]
  );
  const loaded = status === 'ready';
  const [selectedId, setSelectedId] = React.useState<RegionId>('main');
  const [hoveredId, setHoveredId] = React.useState<RegionId | null>(null);
  const reducedMotion = useReducedMotion();
  const darkMode = useResolvedDarkMode();
  const hoveredIdRef = React.useRef<RegionId | null>(null);
  const manualPauseUntilRef = React.useRef(0);
  const selectedRegion =
    REGIONS.find((region) => region.id === selectedId) ?? REGIONS[0];
  const splitIndex = Math.ceil(REGIONS.length / 2);
  const leftRegions = REGIONS.slice(0, splitIndex);
  const rightRegions = REGIONS.slice(splitIndex);

  React.useEffect(() => {
    hoveredIdRef.current = hoveredId;
  }, [hoveredId]);

  React.useEffect(() => {
    if (reducedMotion) {
      return;
    }

    const interval = window.setInterval(() => {
      if (hoveredIdRef.current || Date.now() < manualPauseUntilRef.current) {
        return;
      }

      setSelectedId((current) => {
        const currentIndex = REGIONS.findIndex(
          (region) => region.id === current
        );
        const nextIndex = (currentIndex + 1) % REGIONS.length;

        return REGIONS[nextIndex]?.id ?? 'main';
      });
    }, 3200);

    return () => window.clearInterval(interval);
  }, [reducedMotion]);

  const handleSelectRegion = React.useCallback((region: RegionId) => {
    manualPauseUntilRef.current = Date.now() + 10000;
    setSelectedId(region);
  }, []);

  const pauseRegionCycle = React.useCallback(() => {
    manualPauseUntilRef.current = Date.now() + 10000;
  }, []);

  const handleHoverRegion = React.useCallback((region: RegionId | null) => {
    hoveredIdRef.current = region;
    setHoveredId(region);
  }, []);

  return (
    <main className="min-h-screen bg-slate-50 text-slate-950 dark:bg-[#030711] dark:text-white">
      <section className="mx-auto max-w-[1540px] px-4 py-5 sm:px-6 lg:px-8">
        <section className="relative min-h-[calc(100svh-112px)] overflow-hidden rounded-lg border border-slate-900/10 bg-[linear-gradient(135deg,#eef8ff,#f8fbff_52%,#eaf4ff)] shadow-[0_20px_80px_rgba(15,23,42,0.08)] dark:border-white/12 dark:bg-[linear-gradient(135deg,#050a15,#08111f_52%,#020617)] dark:shadow-none">
          <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(15,23,42,0.05)_1px,transparent_1px),linear-gradient(0deg,rgba(15,23,42,0.04)_1px,transparent_1px)] bg-[size:64px_64px] dark:bg-[linear-gradient(90deg,rgba(148,163,184,0.055)_1px,transparent_1px),linear-gradient(0deg,rgba(148,163,184,0.045)_1px,transparent_1px)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_51%_51%,rgba(14,116,144,0.14),transparent_36%)] dark:bg-[radial-gradient(circle_at_51%_51%,rgba(14,165,233,0.17),transparent_36%)]" />
          {renderData ? (
            <CommandBrainCanvas
              data={renderData}
              selectedId={selectedId}
              hoveredId={hoveredId}
              darkMode={darkMode}
            />
          ) : (
            <BrainMapFallback
              title={
                status === 'error'
                  ? 'Cortex map unavailable'
                  : 'Loading command cortex'
              }
              detail={
                status === 'error'
                  ? 'The region list is still available while the live map waits for a refresh.'
                  : 'Preparing the single interactive brain view.'
              }
            />
          )}

          <div
            aria-hidden="true"
            className="hidden"
          >
            {REGIONS.map((region) => (
              <RegionLabel
                key={region.id}
                region={region}
                selected={region.id === selectedId}
                onSelect={handleSelectRegion}
                onHover={handleHoverRegion}
              />
            ))}
          </div>

          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_48%,transparent_36%,rgba(255,255,255,0.04)_70%,rgba(226,232,240,0.55)_100%)] dark:bg-[radial-gradient(circle_at_50%_48%,transparent_36%,rgba(3,7,18,0.035)_70%,rgba(3,7,18,0.28)_100%)]" />
          <ActiveRegionCard
            region={selectedRegion}
            onPause={pauseRegionCycle}
            className="absolute right-5 top-5 z-30 sm:right-6 sm:top-6 lg:right-8 lg:top-8"
          />
          <div className="pointer-events-none absolute left-5 top-5 z-30 hidden max-w-[calc(100%-29rem)] flex-wrap gap-2 sm:left-6 sm:top-6 lg:left-8 lg:top-8 lg:flex">
            <span className="rounded-md border border-slate-900/10 bg-white/50 px-2.5 py-1.5 text-xs font-semibold text-slate-700 backdrop-blur-sm dark:border-white/12 dark:bg-black/24 dark:text-slate-300">
              {loaded ? 'One brain view' : 'Loading cortex map'}
            </span>
            <span className="rounded-md border border-slate-900/10 bg-white/50 px-2.5 py-1.5 text-xs font-semibold text-slate-700 backdrop-blur-sm dark:border-white/12 dark:bg-black/24 dark:text-slate-300">
              Interactive cortex
            </span>
            <span className="rounded-md border border-slate-900/10 bg-white/50 px-2.5 py-1.5 text-xs font-semibold text-slate-700 backdrop-blur-sm dark:border-white/12 dark:bg-black/24 dark:text-slate-300">
              Click a card to focus
            </span>
          </div>

          <div className="pointer-events-none relative z-20 flex min-h-[calc(100svh-112px)] flex-col p-5 sm:p-6 lg:p-8">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="max-w-[29rem] pt-[19rem]">
                <Link
                  href="/command-cortex-lab"
                  className="pointer-events-auto inline-flex items-center gap-2 text-sm font-semibold text-slate-600 transition hover:text-slate-950 dark:text-slate-300 dark:hover:text-white"
                >
                  Hero lab
                  <ChevronRightIcon className="size-4" />
                  Centered cortex
                </Link>
                <p className="mt-4 text-sm font-semibold text-slate-600 dark:text-slate-300">
                  Viventium
                </p>
                <h1 className="mt-2 text-[44px] font-semibold leading-[0.95] text-slate-950 sm:text-[52px] lg:text-[56px] dark:text-white">
                  Command Cortex v3.
                </h1>
                <p className="mt-3 max-w-[27rem] text-lg font-semibold leading-6 text-slate-900 dark:text-white">
                  One conscious AI, many specialized systems waking only when
                  useful.
                </p>
                <p className="mt-2 max-w-[28rem] text-sm leading-6 text-slate-700 dark:text-slate-300">
                  A brain-inspired map of how Viventium turns specialized AI
                  systems into practical product capabilities.
                </p>
              </div>

              <div />
            </div>

            <LayoutGroup id="command-cortex-v3-region-cards">
              <div className="mt-0 grid flex-1 gap-3 lg:grid-cols-[292px_minmax(0,1fr)_292px] xl:grid-cols-[310px_minmax(0,1fr)_310px]">
                <div className="grid gap-2 self-center lg:-translate-y-6">
                  {leftRegions.map((region) => (
                    <RegionButton
                      key={region.id}
                      region={region}
                      selected={region.id === selectedId}
                      onSelect={handleSelectRegion}
                      onHover={handleHoverRegion}
                    />
                  ))}
                </div>

                <div className="hidden lg:block" />

                <div className="grid gap-2 self-center lg:-translate-y-6">
                  {rightRegions.map((region) => (
                    <RegionButton
                      key={region.id}
                      region={region}
                      selected={region.id === selectedId}
                      onSelect={handleSelectRegion}
                      onHover={handleHoverRegion}
                    />
                  ))}
                </div>
              </div>
            </LayoutGroup>
          </div>
        </section>
      </section>
    </main>
  );
}
